import type { PhotoSearchStreamEventDto } from '../types/photoSearch';

const supportedEventTypes = new Set(['ack', 'stage', 'delta', 'final', 'error', 'done']);

const parseSseBlock = (block: string): PhotoSearchStreamEventDto => {
  const lines = block.split(/\r?\n/);
  const eventType = lines
    .find((line) => line.startsWith('event:'))
    ?.slice('event:'.length)
    .trim();
  const dataLines = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trimStart());

  if (!eventType || !supportedEventTypes.has(eventType) || dataLines.length === 0) {
    throw new Error('Photo-search SSE event is invalid');
  }

  const rawData = dataLines.join('\n');
  let data: unknown;
  try {
    data = JSON.parse(rawData);
  } catch {
    data = rawData;
  }
  return { type: eventType, data } as PhotoSearchStreamEventDto;
};

export const parsePhotoSearchSseEvents = (rawStream: string): PhotoSearchStreamEventDto[] =>
  rawStream
    .trim()
    .split(/\r?\n\r?\n/)
    .filter(Boolean)
    .map(parseSseBlock);

export const createPhotoSearchSseDecoder = () => {
  let buffer = '';
  let terminalEventSeen = false;

  const emitCompleteBlocks = (): PhotoSearchStreamEventDto[] => {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const blocks = normalized.split('\n\n');
    buffer = blocks.pop() ?? '';
    return blocks.filter(Boolean).map((block) => {
      const event = parseSseBlock(block);
      terminalEventSeen ||= event.type === 'done' || event.type === 'error';
      return event;
    });
  };

  return {
    push(chunk: string): PhotoSearchStreamEventDto[] {
      buffer += chunk;
      return emitCompleteBlocks();
    },
    finish(): PhotoSearchStreamEventDto[] {
      const events = emitCompleteBlocks();
      if (buffer.trim()) {
        const event = parseSseBlock(buffer.trim());
        terminalEventSeen ||= event.type === 'done' || event.type === 'error';
        events.push(event);
        buffer = '';
      }
      if (!terminalEventSeen) {
        throw new Error('Photo-search SSE 缺少终态');
      }
      return events;
    },
  };
};

export const deliverPhotoSearchSseText = (
  rawStream: string,
  onEvent: (event: PhotoSearchStreamEventDto) => void
): void => {
  const decoder = createPhotoSearchSseDecoder();
  decoder.push(rawStream).forEach(onEvent);
  decoder.finish().forEach(onEvent);
};
